"use client";

import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { ClipboardIcon } from "@shared/src/components/assets/icons/ClipboardIcon";
import { Button } from "@shared/src/components/ui/button";
import { Spinner } from "@shared/src/components/common/misc/Spinner";
import { P2P_PAGE_EXTERNAL } from "@shared/src/config/page-links";
import { IoIosInformationCircle } from "react-icons/io";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/src/components/ui/tooltip";
import { Chip } from "../../ui/chip";

interface QuickTradeRateAdjustProps {
  rate: number | null;
  isLoading: boolean;
  noAds: boolean;
  manualRate: number | null;
  onManuRateChange: (newRate: number) => void;
  adsCount: number;
  onAdjustOptions?: () => void;
  needsReestimate?: boolean;
  onReestimate?: () => void;
}

export function QuickTradeRateAdjust({
  rate,
  isLoading,
  noAds,
  manualRate,
  onManuRateChange,
  adsCount,
  onAdjustOptions,
  needsReestimate = false,
  onReestimate,
}: QuickTradeRateAdjustProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  // Auto-open dropdown when noAds becomes true
  useEffect(() => {
    if (noAds) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  }, [noAds]);

  const useManualRate = Boolean(manualRate && manualRate !== rate);

  const effectiveRate = useManualRate ? manualRate : rate;
  const currentRate = useManualRate ? manualRate ?? 0 : rate ?? 0;

  // Handle rate adjustment
  const adjustRate = (increment: boolean) => {
    const newRate = Math.max(0, currentRate + (increment ? 0.01 : -0.01));
    onManuRateChange(newRate);
  };

  const handleManualRateInput = (value: string) => {
    const numValue = parseFloat(value.replace("GHS ", "")) || 0;
    onManuRateChange(numValue);
  };

  const useRecommended = () => {
    onManuRateChange(rate || 0);
  };

  const handleAdjustOptions = () => {
    setIsDropdownOpen(false);
    onAdjustOptions?.();
  };

  return (
    <div className="w-full text-text-primary">
      {/* Rate Selector (Wrapper) */}
      <div
        className={`w-full bg-surfaces-bg-secondary-darker px-4 py-4 rounded-5xl border border-border-tertiary ${
          isDropdownOpen ? "rounded-b-none border-b-0 bg-transparent" : ""
        }`}
      >
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setIsDropdownOpen(prev => !prev)}
        >
          <div className="flex items-center gap-2">
            <span className="text-text-tertiary text-small font-bold">Indicative Rate:</span>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-text-tertiary hover:text-text-primary focus:outline-none"
                    onClick={event => event.stopPropagation()}
                  >
                    <IoIosInformationCircle size={16} />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="start"
                  className="max-w-[220px] text-text-primary bg-surfaces-bg-secondary"
                >
                  The displayed rate is indicative only. The final exchange rate is confirmed when a merchant accepts
                  your trade.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-text-tertiary">
              {isLoading ? (
                <span className="text-text-primary flex items-center gap-2">
                  <Spinner size={16} className="text-text-primary" />
                  Estimating rate...
                </span>
              ) : effectiveRate !== null ? (
                <div className="flex items-center gap-2">
                  <span className={`text-text-primary ${useManualRate ? "text-yellow-500" : ""}`}>
                    GHS {effectiveRate.toFixed(2)} {useManualRate && "(Manual)"}
                  </span>
                  {needsReestimate && onReestimate && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        onReestimate();
                      }}
                      className="flex items-center gap-1 text-text-brand hover:text-text-brand-hover text-xs"
                    >
                      <RefreshCw size={12} />
                      Reestimate
                    </button>
                  )}
                </div>
              ) : (
                <span className="text-text-primary">-</span>
              )}
            </span>
            {isDropdownOpen ? (
              <ChevronUp className="w-4 h-4 text-text-primary" />
            ) : (
              <ChevronDown className="w-4 h-4 text-text-primary" />
            )}
          </div>
        </div>
      </div>

      {/* Dropdown Section with TailwindCSS Animations */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isDropdownOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="w-full p-3 rounded-b-lg border border-t-0 border-gray-700">
          {noAds ? (
            <div className="flex flex-col items-center text-center py-6">
              <ClipboardIcon className="mb-4" />
              <h3 className="text-text-primary font-bold text-lg">No ads available!</h3>
              <p className="text-text-secondary mt-2 mb-4">Please try different options and amounts or go to P2P</p>
              <div className="flex gap-3 justify-center items-center">
                <Button variant="outline" size="sm" onClick={handleAdjustOptions} className="px-6">
                  Adjust Options
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-3">
                <button
                  type="button"
                  onClick={useRecommended}
                  className="w-full md:w-auto bg-surfaces-bg-secondary px-4 py-3 rounded-full text-smallall text-text-tertiary"
                >
                  Use Recommended
                </button>
                <span className="text-text-primary text-smallall">or</span>
                <div className="w-full md:w-auto flex items-center justify-between border border-border-tertiary bg-transparent px-4 py-3 rounded-5xl">
                  <button
                    type="button"
                    className="text-text-primary hover:text-text-primary bg-surfaces-bg-tertiary rounded-full p-1 h-6 w-6 flex items-center justify-center"
                    onClick={() => adjustRate(false)}
                  >
                    -
                  </button>
                  <input
                    type="text"
                    value={`GHS ${currentRate.toFixed(2)}`}
                    onChange={e => handleManualRateInput(e.target.value)}
                    className="w-24 bg-transparent text-center text-text-primary outline-none"
                  />
                  <button
                    type="button"
                    className="text-text-primary hover:text-text-primary  bg-surfaces-bg-tertiary rounded-full p-1 h-6 w-6 flex items-center justify-center"
                    onClick={() => adjustRate(true)}
                  >
                    +
                  </button>
                </div>
              </div>
              <p className="text-small text-text-primary flex justify-between px-2">
                <span className="text-text-tertiary">Number of ads:</span>
                <span className="text-text-primary">{adsCount}</span>
              </p>
              <div className="animate-in slide-in-from-top-5 fade-in-0 duration-300">
                <div className="flex flex-col sm:flex-row items-center justify-between text-small text-text-tertiary">
                  <div className="flex items-center gap-2 rounded-2xl py-3 px-2">
                    <span>Fee rate:</span>
                    <span className="flex items-center gap-1 text-text-primary">
                      <IoIosInformationCircle size={16} />
                      0.1000%
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>Estimated gas fee</span>
                    <IoIosInformationCircle size={16} className="text-text-primary" />
                    <span>
                      <Chip variant={"success"}>Free</Chip>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
